import React, { useState, useEffect, useRef } from 'react'

import PropTypes from 'prop-types'
import { toast } from 'react-toastify'

import { updateCliente, uploadClienteLogo } from '../api/admin.api'

import './edit-client.css'
import './edit-client-modal.css'

const EditClient = ({ isOpen, onClose, cliente, onSave }) => {
  const [formData, setFormData] = useState({
    nombre_empresa: '',
    logo_url: '',
    investigacion_habilitada: false,
  })
  const [loading, setLoading] = useState(false)
  const [selectedLogoFile, setSelectedLogoFile] = useState(null)
  const [logoPreviewFailed, setLogoPreviewFailed] = useState(false)

  const fileInputRef = useRef(null)

  useEffect(() => {
    if (cliente) {
      setFormData({
        nombre_empresa: cliente.nombre_empresa ?? '',
        logo_url: cliente.logo_url ?? '',
        investigacion_habilitada: cliente.investigacion_habilitada ?? false,
      })
    } else {
      setFormData({
        nombre_empresa: '',
        logo_url: '',
        investigacion_habilitada: false,
      })
    }

    setSelectedLogoFile(null)
    setLogoPreviewFailed(false)

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [cliente])

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleLogoButtonClick = () => {
    if (loading) return
    fileInputRef.current?.click()
  }

  const handleLogoFileChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    const allowedTypes = new Set([
      'image/png',
      'image/jpeg',
      'image/webp',
      'image/svg+xml',
    ])

    if (!allowedTypes.has(file.type)) {
      toast.error('Formato de logo no soportado')
      e.target.value = ''
      return
    }

    setSelectedLogoFile(file)
    setLogoPreviewFailed(false)

    const previewUrl = URL.createObjectURL(file)
    setFormData((prev) => ({
      ...prev,
      logo_url: previewUrl,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!cliente?.id) {
      toast.error('Cliente inválido')
      return
    }

    const nombreEmpresa = formData.nombre_empresa.trim()

    if (!nombreEmpresa) {
      toast.error('El nombre de la empresa es obligatorio')
      return
    }

    setLoading(true)

    try {
      let updatedCliente = cliente

      const patches = {}
      if (nombreEmpresa !== (cliente.nombre_empresa ?? '').trim()) {
        patches.nombre_empresa = nombreEmpresa
      }
      if (formData.investigacion_habilitada !== (cliente.investigacion_habilitada ?? false)) {
        patches.investigacion_habilitada = formData.investigacion_habilitada
      }
      if (Object.keys(patches).length > 0) {
        updatedCliente = await updateCliente(cliente.id, patches)
      }

      if (selectedLogoFile) {
        updatedCliente = await uploadClienteLogo(cliente.id, selectedLogoFile)
      }

      toast.success('Cliente actualizado correctamente')
      onSave(updatedCliente)
    } catch (err) {
      const message =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        'Error al actualizar el cliente'
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (loading) return

    setFormData({
      nombre_empresa: '',
      logo_url: '',
    })
    setSelectedLogoFile(null)
    setLogoPreviewFailed(false)

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }

    onClose()
  }

  if (!isOpen) return null

  const logoFallback =
    cliente?.nombre_empresa
      ?.trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0] ?? '')
      .join('')
      .toUpperCase() || 'CL'

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-scroll-body">
        <div className="edit-client-thq-editclient-elm">
          <div className="edit-client-thq-title-elm">
            <span className="edit-client-thq-text-elm1 headline01">
              Editar Cliente
            </span>
            <span className="edit-client-thq-text-elm2 bodystandard">
              Actualiza la información de la empresa
            </span>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="edit-client-thq-fields-elm">
              <div className="edit-client-thq-name-elm1">
                <span className="edit-client-thq-text-elm3 bodysmall">
                  Nombre de la empresa
                </span>
                <div className="edit-client-thq-field-elm1">
                  <input
                    type="text"
                    value={formData.nombre_empresa}
                    onChange={(e) =>
                      handleChange('nombre_empresa', e.target.value)
                    }
                    className="bodystandard"
                    style={{ width: '100%', border: 'none', outline: 'none' }}
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="edit-client-thq-logo-elm1">
                <span className="edit-client-thq-text-elm5 bodysmall">
                  Logo actual
                </span>
                <div className="edit-client-thq-field-elm2">
                  {formData.logo_url && !logoPreviewFailed ? (
                    <img
                      alt="logo"
                      src={formData.logo_url}
                      className="edit-client-thq-logo-elm2"
                      onError={() => setLogoPreviewFailed(true)}
                    />
                  ) : (
                    <span className="bodysmall">{formData.logo_url ? logoFallback : 'Sin logo'}</span>
                  )}

                  <button
                    type="button"
                    className="edit-client-thq-buttonupload-elm"
                    onClick={handleLogoButtonClick}
                    disabled={loading}
                  >
                    <img
                      alt="upload"
                      src="/upload.svg"
                      className="edit-client-thq-frame-elm"
                    />
                    <span className="edit-client-thq-text-elm6">
                      {selectedLogoFile ? 'Logo seleccionado' : 'Subir logo'}
                    </span>
                  </button>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".png,.jpg,.jpeg,.webp,.svg,image/png,image/jpeg,image/webp,image/svg+xml"
                    style={{ display: 'none' }}
                    onChange={handleLogoFileChange}
                  />
                </div>
              </div>

              <div className="edit-client-thq-name-elm2">
                <span className="edit-client-thq-text-elm7 bodysmall">
                  Identificador Marvin
                </span>
                <div className="edit-client-thq-field-elm3">
                  <span className="edit-client-thq-text-elm8 bodystandard">
                    {cliente?.identificador_marvin ?? '—'}
                  </span>
                </div>
              </div>

              <div className="edit-client-investigacion-row">
                <div>
                  <span className="edit-client-thq-text-elm7 bodysmall">
                    Acceso a investigación
                  </span>
                  <span className="edit-client-investigacion-hint caption">
                    Permite ao cliente descarregar relatórios de investigação
                  </span>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={formData.investigacion_habilitada}
                  className={`edit-client-toggle-switch${formData.investigacion_habilitada ? ' edit-client-toggle-switch--on' : ''}`}
                  onClick={() => handleChange('investigacion_habilitada', !formData.investigacion_habilitada)}
                  disabled={loading}
                />
              </div>
            </div>

            <button
              type="submit"
              className="edit-client-thq-buttonsave-elm"
              disabled={loading}
            >
              <span className="edit-client-thq-text-elm9">
                {loading ? 'Guardando...' : 'Guardar cambios'}
              </span>
            </button>
          </form>
        </div>
        </div>

        <button
          type="button"
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            background: 'none',
            border: 'none',
            fontSize: 20,
            cursor: loading ? 'default' : 'pointer',
            lineHeight: 1,
          }}
          aria-label="Cerrar"
          disabled={loading}
        >
          ✕
        </button>
      </div>
    </div>
  )
}

EditClient.defaultProps = {
  cliente: null,
}

EditClient.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  cliente: PropTypes.object,
  onSave: PropTypes.func.isRequired,
}

export default EditClient
